import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swords, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2K PR 1 — Player Rivalries strip.
 *
 * Surfaces the player's top 3 squad rivalries with anticipation copy.
 * Mounted on the Player Dashboard.  Tone: tribal commentary, never analytics.
 */
export default function ArenaxPlayerRivalries({ limit = 3 }) {
  const [rivalries, setRivalries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/arenax/player/rivalries?limit=${limit}`)
      .then(r => setRivalries(r.data?.rivalries || []))
      .catch(() => setRivalries([]))
      .finally(() => setLoading(false));
  }, [limit]);

  if (loading || rivalries.length === 0) return null;

  return (
    <div className="rounded-2xl border border-rose-500/20 p-5"
      data-testid="arenax-player-rivalries"
      style={{ background: 'linear-gradient(160deg, rgba(244,63,94,0.06), var(--ax-card-bg-soft))' }}>
      <div className="flex items-center gap-2 mb-3">
        <Swords className="h-4 w-4 text-rose-300" />
        <span className="text-[10px] tracking-[0.3em] text-rose-300 font-bold uppercase">
          Your crew's rivalries
        </span>
      </div>
      <div className="space-y-3">
        {rivalries.map(r => (
          <Link key={`${r.squad_id}-${r.opponent_id}`}
            to={`/arenax/squads/${r.squad_id}`}
            data-testid={`arenax-player-rivalry-row-${r.opponent_id}`}
            className="block rounded-lg p-3 bg-slate-900/50 border border-slate-800 hover:border-rose-500/40 transition-all group">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  {r.squad_name}
                </div>
                <div className="text-sm font-bold text-slate-100 truncate">
                  vs {r.opponent_name}
                </div>
              </div>
              <span className="text-xs font-black tabular-nums shrink-0">
                <span className="text-emerald-300">{r.wins}</span>
                <span className="text-slate-600 mx-1">·</span>
                <span className="text-slate-400">{r.draws}</span>
                <span className="text-slate-600 mx-1">·</span>
                <span className="text-rose-300">{r.losses}</span>
              </span>
            </div>
            {r.anticipation && (
              <p className="text-[11px] text-rose-100 italic leading-snug"
                data-testid={`arenax-player-rivalry-anticipation-${r.opponent_id}`}>
                {r.anticipation}
              </p>
            )}
            {r.last_match_label && (
              <div className="mt-1 flex items-center justify-between gap-2 text-[9px] uppercase tracking-wider text-slate-500 font-bold">
                <span>Last: {r.last_match_label}</span>
                <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
